import React from 'react';
import {
  AdminEventCategory,
  AdminEventDto,
  EventManagerDto,
  PageDto,
  addAdminEventManagers,
  createAdminEvent,
  getAdminEventManagers,
  getAdminEvents,
  getAdminNotificationCommunities,
  removeAdminEventManager,
  useApiClient,
} from '../api/client';
import { UserSearchResult, UserTypeahead } from '../components/UserTypeahead';
import { EmptyRow, FilterBar, InfoTile, PageHeader, PageShell, PaginationBar, StatusBadge } from '../components/ui';
import { useI18n } from '../i18n/I18nProvider';
import { useToast } from '../toast/ToastProvider';
import { formatDateTime } from '../utils/dateFormat';

// mapbox-gl is loaded via CDN in public/index.html
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const mapboxgl: any;

const PAGE_SIZE = 10;
const CATEGORIES: AdminEventCategory[] = ['SPORTS', 'CULTURE', 'MUSIC', 'FESTIVAL', 'MEETING', 'WORKSHOP', 'OTHER'];

type CommunityOption = {
  id: string;
  name: string;
};

type MapboxSuggestion = {
  id: string;
  name: string;
  description: string;
};

type SelectedEventLocation = {
  publicName: string;
  addressLine?: string | null;
  longitude: number;
  latitude: number;
  rawFeatureJson?: string | null;
};

const createSessionToken = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Use fallback below.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const stripAustria = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const stripped = trimmed.replace(/\s*,\s*(Austria|Österreich|Oesterreich)\s*$/i, '').trim();
  return stripped || undefined;
};

const withPublicName = (publicName: string, context?: string | null) => {
  const trimmedContext = context?.trim();
  if (!trimmedContext) return undefined;
  if (!publicName) return trimmedContext;
  return trimmedContext.toLocaleLowerCase().startsWith(publicName.toLocaleLowerCase())
    ? trimmedContext
    : `${publicName}, ${trimmedContext}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapboxAddressLine = (feature: any, suggestion: MapboxSuggestion, publicName: string) => {
  const properties = feature?.properties ?? {};
  const contextualAddress =
    properties.full_address ||
    properties.place_name ||
    feature?.place_name ||
    withPublicName(publicName, properties.place_formatted) ||
    suggestion.description;

  return stripAustria(String(contextualAddress || ''));
};

function toLocalInputValue(value: Date) {
  const offsetMs = value.getTimezoneOffset() * 60_000;
  return new Date(value.getTime() - offsetMs).toISOString().slice(0, 16);
}

function toIsoFromLocalInput(value: string) {
  return value ? new Date(value).toISOString() : null;
}

function formatManagerName(user: EventManagerDto | UserSearchResult) {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name ? `${name} · ${user.email}` : user.email;
}

export const EventsAdminPage: React.FC = () => {
  const { request } = useApiClient();
  const { language } = useI18n();
  const { showError, showSuccess } = useToast();
  const [communities, setCommunities] = React.useState<CommunityOption[]>([]);
  const [page, setPage] = React.useState<PageDto<AdminEventDto> | null>(null);
  const [q, setQ] = React.useState('');
  const [communityId, setCommunityId] = React.useState('');
  const [pageNumber, setPageNumber] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [showCreate, setShowCreate] = React.useState(false);
  const [expandedEventId, setExpandedEventId] = React.useState<string | null>(null);
  const [managersByEvent, setManagersByEvent] = React.useState<Record<string, EventManagerDto[]>>({});
  const [pendingManagersByEvent, setPendingManagersByEvent] = React.useState<Record<string, UserSearchResult[]>>({});
  const [savingManagerEventId, setSavingManagerEventId] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  const defaultStart = React.useMemo(() => {
    const start = new Date();
    start.setDate(start.getDate() + 7);
    start.setMinutes(0, 0, 0);
    return toLocalInputValue(start);
  }, []);
  const [form, setForm] = React.useState({
    communityId: '',
    name: '',
    description: '',
    category: 'OTHER' as AdminEventCategory,
    startTime: defaultStart,
    endTime: '',
    registrationRequired: false,
    registrationDeadline: '',
    voucherEmailText: '',
    voucherEnabled: false,
  });
  const [createManagers, setCreateManagers] = React.useState<UserSearchResult[]>([]);
  const [locationQuery, setLocationQuery] = React.useState('');
  const [locationSuggestions, setLocationSuggestions] = React.useState<MapboxSuggestion[]>([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = React.useState(false);
  const [locationLoading, setLocationLoading] = React.useState(false);
  const [selectedLocation, setSelectedLocation] = React.useState<SelectedEventLocation | null>(null);
  const [locationError, setLocationError] = React.useState<string | null>(null);
  const mapContainerRef = React.useRef<HTMLDivElement | null>(null);
  const mapRef = React.useRef<any | null>(null);
  const markerRef = React.useRef<any | null>(null);
  const sessionTokenRef = React.useRef(createSessionToken());
  const mapboxToken = (process.env.REACT_APP_MAPBOX_TOKEN || '').trim();

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminEvents(request, {
        page: pageNumber,
        size: PAGE_SIZE,
        query: q,
        communityId,
      });
      setPage(data);
      if (data.totalPages > 0 && pageNumber >= data.totalPages) {
        setPageNumber(data.totalPages - 1);
      }
    } catch (err) {
      showError((err as Error)?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [communityId, pageNumber, q, request, showError]);

  React.useEffect(() => {
    getAdminNotificationCommunities(request)
      .then(setCommunities)
      .catch((err) => showError((err as Error)?.message || 'Failed to load communities'));
  }, [request, showError]);

  React.useEffect(() => {
    const timeout = window.setTimeout(load, 250);
    return () => window.clearTimeout(timeout);
  }, [load]);

  React.useEffect(() => {
    if (!showCreate || !mapboxToken || !mapContainerRef.current || mapRef.current) return;

    mapboxgl.accessToken = mapboxToken;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [11.576124, 48.137154],
      zoom: 11,
    });
    mapRef.current = map;

    map.on('click', (event: any) => {
      const longitude = Number(event.lngLat.lng);
      const latitude = Number(event.lngLat.lat);
      const coordinateName = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
      setSelectedLocation({
        publicName: coordinateName,
        addressLine: null,
        longitude,
        latitude,
        rawFeatureJson: null,
      });
      setLocationQuery(coordinateName);
      setShowLocationSuggestions(false);
    });

    return () => {
      markerRef.current = null;
      mapRef.current = null;
      map.remove();
    };
  }, [mapboxToken, showCreate]);

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedLocation) return;

    if (!markerRef.current) {
      markerRef.current = new mapboxgl.Marker({ color: '#6B2BD9' });
    }
    markerRef.current
      .setLngLat([selectedLocation.longitude, selectedLocation.latitude])
      .addTo(map);
    map.flyTo({ center: [selectedLocation.longitude, selectedLocation.latitude], zoom: 14 });
  }, [selectedLocation]);

  React.useEffect(() => {
    if (!showLocationSuggestions || locationQuery.trim().length < 3 || !mapboxToken) {
      setLocationSuggestions([]);
      return;
    }

    const controller = new AbortController();
    const handle = window.setTimeout(async () => {
      setLocationLoading(true);
      setLocationError(null);
      try {
        const url = new URL('https://api.mapbox.com/search/searchbox/v1/suggest');
        url.searchParams.set('q', locationQuery.trim());
        url.searchParams.set('access_token', mapboxToken);
        url.searchParams.set('language', language);
        url.searchParams.set('country', 'de,at,ch');
        url.searchParams.set('limit', '8');
        url.searchParams.set('types', 'poi,address,street,district,place,locality,neighborhood');
        url.searchParams.set('session_token', sessionTokenRef.current);
        const response = await fetch(url.toString(), { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const suggestions: MapboxSuggestion[] = (data.suggestions || []).map((suggestion: any) => ({
          id: String(suggestion.mapbox_id),
          name: String(suggestion.name || suggestion.full_address || suggestion.place_formatted || ''),
          description: String(suggestion.full_address || suggestion.place_formatted || ''),
        }));
        setLocationSuggestions(suggestions);
      } catch (error: any) {
        if (error.name === 'AbortError') return;
        setLocationSuggestions([]);
        setLocationError('Location search failed. Please try again.');
      } finally {
        setLocationLoading(false);
      }
    }, 250);

    return () => {
      window.clearTimeout(handle);
      controller.abort();
    };
  }, [language, locationQuery, mapboxToken, showLocationSuggestions]);

  async function resolveLocationSuggestion(suggestion: MapboxSuggestion) {
    if (!mapboxToken) return;
    try {
      const url = new URL(`https://api.mapbox.com/search/searchbox/v1/retrieve/${encodeURIComponent(suggestion.id)}`);
      url.searchParams.set('access_token', mapboxToken);
      url.searchParams.set('session_token', sessionTokenRef.current);
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const feature = data.feature || (Array.isArray(data.features) ? data.features[0] : null);
      const coordinates = feature?.geometry?.coordinates;
      if (!Array.isArray(coordinates) || coordinates.length < 2) return;

      const publicName = String(
        feature?.properties?.name_preferred ||
        feature?.properties?.name ||
        feature?.name ||
        suggestion.name ||
        ''
      ).trim();
      const addressLine = mapboxAddressLine(feature, suggestion, publicName);

      setSelectedLocation({
        publicName: publicName || addressLine || suggestion.name,
        addressLine: addressLine || null,
        longitude: Number(coordinates[0]),
        latitude: Number(coordinates[1]),
        rawFeatureJson: JSON.stringify(feature),
      });
      setLocationQuery(addressLine || publicName || suggestion.name);
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      setLocationError(null);
      sessionTokenRef.current = createSessionToken();
    } catch (error) {
      setLocationError((error as Error)?.message || 'Location retrieve failed. Please try again.');
    }
  }

  function resetCreateForm() {
    setForm((prev) => ({
      ...prev,
      name: '',
      description: '',
      startTime: defaultStart,
      endTime: '',
      category: 'OTHER',
      registrationRequired: false,
      registrationDeadline: '',
      voucherEmailText: '',
      voucherEnabled: false,
    }));
    setCreateManagers([]);
    setSelectedLocation(null);
    setLocationQuery('');
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
    setLocationError(null);
    sessionTokenRef.current = createSessionToken();
  }

  async function submitEvent(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedLocation) {
      showError('Please select an event location.');
      return;
    }
    setCreating(true);
    try {
      await createAdminEvent(request, {
        communityId: form.communityId,
        name: form.name,
        description: form.description,
        startTime: toIsoFromLocalInput(form.startTime) || new Date().toISOString(),
        endTime: toIsoFromLocalInput(form.endTime),
        location: {
          publicName: selectedLocation.publicName,
          addressLine: selectedLocation.addressLine || null,
          longitude: selectedLocation.longitude,
          latitude: selectedLocation.latitude,
          rawFeatureJson: selectedLocation.rawFeatureJson || null,
        },
        category: form.category,
        registrationRequired: form.registrationRequired,
        registrationDeadline: toIsoFromLocalInput(form.registrationDeadline),
        voucherEmailText: form.voucherEmailText.trim() || null,
        voucherEnabled: form.voucherEnabled,
        managerUserIds: createManagers.map((manager) => manager.id),
      });
      showSuccess('Event created');
      setShowCreate(false);
      resetCreateForm();
      await load();
    } catch (err) {
      showError((err as Error)?.message || 'Failed to create event');
    } finally {
      setCreating(false);
    }
  }

  async function expandEvent(eventId: string) {
    if (expandedEventId === eventId) {
      setExpandedEventId(null);
      return;
    }
    setExpandedEventId(eventId);
    if (managersByEvent[eventId]) return;
    try {
      const managers = await getAdminEventManagers(request, eventId);
      setManagersByEvent((prev) => ({ ...prev, [eventId]: managers }));
    } catch (err) {
      showError((err as Error)?.message || 'Failed to load event managers');
    }
  }

  async function addManagers(eventId: string) {
    const pending = pendingManagersByEvent[eventId] || [];
    if (!pending.length) return;
    setSavingManagerEventId(eventId);
    try {
      const managers = await addAdminEventManagers(request, eventId, pending.map((user) => user.id));
      setManagersByEvent((prev) => ({ ...prev, [eventId]: managers }));
      setPendingManagersByEvent((prev) => ({ ...prev, [eventId]: [] }));
      showSuccess('Event managers updated');
    } catch (err) {
      showError((err as Error)?.message || 'Failed to add event managers');
    } finally {
      setSavingManagerEventId(null);
    }
  }

  async function removeManager(eventId: string, userId: string) {
    setSavingManagerEventId(eventId);
    try {
      const managers = await removeAdminEventManager(request, eventId, userId);
      setManagersByEvent((prev) => ({ ...prev, [eventId]: managers }));
      showSuccess('Event manager removed');
    } catch (err) {
      showError((err as Error)?.message || 'Failed to remove event manager');
    } finally {
      setSavingManagerEventId(null);
    }
  }

  function addPendingManager(eventId: string, user: UserSearchResult) {
    setPendingManagersByEvent((prev) => {
      const current = prev[eventId] || [];
      if (current.some((item) => item.id === user.id)) return prev;
      return { ...prev, [eventId]: [...current, user] };
    });
  }

  const events = page?.content || [];

  return (
    <PageShell>
      <PageHeader
        title="Events"
        description="Create customer events and assign event managers who can operate the event dashboard."
        actions={
          <>
            <button className="btn btn-neutral" onClick={load} disabled={loading}>Refresh</button>
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              New event
            </button>
          </>
        }
      />

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <div>
                <h2 className="text-xl font-extrabold text-gray-900">Create event</h2>
                <p className="mt-1 text-sm text-gray-500">Create an event, select its Mapbox location, and assign event dashboard managers.</p>
              </div>
              <button
                type="button"
                className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                onClick={() => {
                  setShowCreate(false);
                  resetCreateForm();
                }}
              >
                Close
              </button>
            </div>

            <form onSubmit={submitEvent} className="grid gap-5 p-6">
              <div className="grid gap-4 lg:grid-cols-2">
                <label className="grid gap-1 text-sm font-semibold text-gray-700">
                  Community
                  <select
                    className="form-control"
                    value={form.communityId}
                    onChange={(e) => setForm((prev) => ({ ...prev, communityId: e.target.value }))}
                    required
                  >
                    <option value="">Select community</option>
                    {communities.map((community) => (
                      <option key={community.id} value={community.id}>{community.name}</option>
                    ))}
                  </select>
                </label>
                <label className="grid gap-1 text-sm font-semibold text-gray-700">
                  Name
                  <input className="form-control" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} required />
                </label>
                <label className="grid gap-1 text-sm font-semibold text-gray-700 lg:col-span-2">
                  Description
                  <textarea className="form-control" rows={3} value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} required />
                </label>
                <label className="grid gap-1 text-sm font-semibold text-gray-700">
                  Start
                  <input className="form-control" type="datetime-local" value={form.startTime} onChange={(e) => setForm((prev) => ({ ...prev, startTime: e.target.value }))} required />
                </label>
                <label className="grid gap-1 text-sm font-semibold text-gray-700">
                  End
                  <input className="form-control" type="datetime-local" value={form.endTime} onChange={(e) => setForm((prev) => ({ ...prev, endTime: e.target.value }))} />
                </label>
                <label className="grid gap-1 text-sm font-semibold text-gray-700">
                  Category
                  <select className="form-control" value={form.category} onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value as AdminEventCategory }))}>
                    {CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
                  </select>
                </label>
              </div>

              <div className="grid gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="text-sm font-semibold text-gray-700">Event location</div>
                {!mapboxToken && (
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-900">
                    Location search is disabled because REACT_APP_MAPBOX_TOKEN is missing.
                  </div>
                )}
                <div className="relative">
                  <input
                    className="form-control w-full"
                    value={locationQuery}
                    onFocus={() => setShowLocationSuggestions(true)}
                    onChange={(e) => {
                      setLocationQuery(e.target.value);
                      setSelectedLocation(null);
                      setShowLocationSuggestions(true);
                      setLocationError(null);
                    }}
                    placeholder="Search place or address"
                    required
                  />
                  {showLocationSuggestions && locationSuggestions.length > 0 && (
                    <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                      {locationSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.id}
                          type="button"
                          className="block w-full border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-gray-50"
                          onClick={() => resolveLocationSuggestion(suggestion)}
                        >
                          <div className="font-semibold text-gray-900">{suggestion.name}</div>
                          {suggestion.description && <div className="mt-1 text-xs text-gray-500">{suggestion.description}</div>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {locationLoading && <div className="text-sm text-gray-500">Searching locations...</div>}
                {locationError && <div className="text-sm font-semibold text-red-600">{locationError}</div>}
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                  <div ref={mapContainerRef} className="h-72 w-full" />
                </div>
                {selectedLocation && (
                  <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
                    <div className="font-semibold">{selectedLocation.publicName}</div>
                    {selectedLocation.addressLine && <div className="mt-1">{selectedLocation.addressLine}</div>}
                    <div className="mt-1 text-xs">
                      {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <label className="flex items-center gap-3 text-sm font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.registrationRequired}
                    onChange={(e) => setForm((prev) => ({ ...prev, registrationRequired: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-secondary focus:ring-secondary"
                  />
                  Registration required
                </label>
                {form.registrationRequired && (
                  <label className="grid gap-1 text-sm font-semibold text-gray-700">
                    Registration deadline
                    <input className="form-control" type="datetime-local" value={form.registrationDeadline} onChange={(e) => setForm((prev) => ({ ...prev, registrationDeadline: e.target.value }))} />
                  </label>
                )}
                <label className="flex items-center gap-3 text-sm font-semibold text-gray-700 lg:col-span-2">
                  <input
                    type="checkbox"
                    checked={form.voucherEnabled}
                    onChange={(e) => setForm((prev) => ({ ...prev, voucherEnabled: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-secondary focus:ring-secondary"
                  />
                  Send drink voucher emails for this event
                </label>
                <label className="grid gap-1 text-sm font-semibold text-gray-700 lg:col-span-2">
                  Voucher email text
                  <textarea
                    className="form-control"
                    rows={4}
                    value={form.voucherEmailText}
                    onChange={(e) => setForm((prev) => ({ ...prev, voucherEmailText: e.target.value }))}
                    placeholder="Main text sent with each free drink voucher after the ride is completed"
                  />
                </label>
              </div>

              <div className="grid gap-3 rounded-lg bg-gray-50 p-4">
                <div className="text-sm font-semibold text-gray-700">Event managers</div>
                <UserTypeahead onSelect={(user) => setCreateManagers((prev) => prev.some((item) => item.id === user.id) ? prev : [...prev, user])} />
                {createManagers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {createManagers.map((user) => (
                      <span key={user.id} className="inline-flex items-center gap-2 rounded-full bg-purple-100 px-3 py-1 text-sm">
                        {formatManagerName(user)}
                        <button type="button" className="text-gray-500 hover:text-gray-700" onClick={() => setCreateManagers((prev) => prev.filter((item) => item.id !== user.id))}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  className="btn btn-neutral"
                  disabled={creating}
                  onClick={() => {
                    setShowCreate(false);
                    resetCreateForm();
                  }}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" disabled={creating || !selectedLocation}>
                  {creating ? 'Creating...' : 'Create event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <FilterBar className="lg:grid-cols-[1fr_280px_auto]">
        <input className="form-control" placeholder="Search events" value={q} onChange={(e) => { setQ(e.target.value); setPageNumber(0); }} />
        <select className="form-control" value={communityId} onChange={(e) => { setCommunityId(e.target.value); setPageNumber(0); }}>
          <option value="">All communities</option>
          {communities.map((community) => (
            <option key={community.id} value={community.id}>{community.name}</option>
          ))}
        </select>
        <button className="btn btn-neutral" onClick={() => { setQ(''); setCommunityId(''); setPageNumber(0); }}>Clear</button>
      </FilterBar>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
        <div className="flex items-center justify-between border-b px-4 py-3 text-sm text-gray-600">
          <span>{loading ? 'Loading events…' : `${page?.totalElements || 0} events`}</span>
          <span>Page {(page?.number || 0) + 1} of {Math.max(page?.totalPages || 1, 1)}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 font-semibold">Event</th>
                <th className="px-4 py-3 font-semibold">Community</th>
                <th className="px-4 py-3 font-semibold">Location</th>
                <th className="px-4 py-3 font-semibold">Ride demand</th>
                <th className="px-4 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => {
                const expanded = expandedEventId === event.id;
                const managers = managersByEvent[event.id] || [];
                const pending = pendingManagersByEvent[event.id] || [];
                return (
                  <React.Fragment key={event.id}>
                    <tr className="border-t border-gray-100 align-top">
                      <td className="px-4 py-4">
                        <div className="font-semibold text-gray-900">{event.name}</div>
                        <div className="mt-1 text-xs text-gray-500">{formatDateTime(event.startTime)}</div>
                        <div className="mt-2"><StatusBadge tone="violet">{event.category}</StatusBadge></div>
                      </td>
                      <td className="px-4 py-4 text-gray-700">{event.communityName}</td>
                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-900">{event.location.publicName}</div>
                        <div className="text-xs text-gray-500">{event.location.addressLine}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="grid grid-cols-2 gap-2">
                          <InfoTile label="Drivers" value={event.driversGoingCount} />
                          <InfoTile label="Looking" value={event.ridersLookingCount} />
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button className="btn btn-neutral" onClick={() => expandEvent(event.id)}>{expanded ? 'Hide' : 'Managers'}</button>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="border-t border-gray-100 bg-gray-50">
                        <td colSpan={5} className="px-4 py-4">
                          <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
                            <div className="rounded-lg border border-gray-200 bg-white p-4">
                              <div className="mb-3 text-sm font-semibold text-gray-700">Assigned event managers</div>
                              {managers.length ? (
                                <div className="grid gap-2">
                                  {managers.map((manager) => (
                                    <div key={manager.id} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
                                      <span className="text-sm">{formatManagerName(manager)}</span>
                                      <button className="text-sm font-semibold text-red-600 hover:text-red-700" disabled={savingManagerEventId === event.id} onClick={() => removeManager(event.id, manager.id)}>Remove</button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500">No event managers assigned.</div>
                              )}
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-white p-4">
                              <div className="mb-3 text-sm font-semibold text-gray-700">Add managers</div>
                              <UserTypeahead onSelect={(user) => addPendingManager(event.id, user)} />
                              {pending.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {pending.map((user) => (
                                    <span key={user.id} className="inline-flex items-center gap-2 rounded-full bg-purple-100 px-3 py-1 text-sm">
                                      {formatManagerName(user)}
                                      <button
                                        type="button"
                                        className="text-gray-500 hover:text-gray-700"
                                        onClick={() => setPendingManagersByEvent((prev) => ({ ...prev, [event.id]: pending.filter((item) => item.id !== user.id) }))}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}
                              <button className="btn btn-primary mt-3" disabled={!pending.length || savingManagerEventId === event.id} onClick={() => addManagers(event.id)}>
                                {savingManagerEventId === event.id ? 'Saving…' : 'Add managers'}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!loading && events.length === 0 && <EmptyRow colSpan={5}>No events match the current filters.</EmptyRow>}
            </tbody>
          </table>
        </div>
        <PaginationBar
          loading={loading}
          pageNumber={pageNumber}
          pageSize={PAGE_SIZE}
          rowCount={events.length}
          totalElements={page?.totalElements}
          isLast={page?.last ?? true}
          onPrevious={() => setPageNumber((prev) => Math.max(0, prev - 1))}
          onNext={() => setPageNumber((prev) => prev + 1)}
          previousLabel="Previous"
          nextLabel="Next"
        />
      </div>
    </PageShell>
  );
};
